import React, { useRef, useState } from 'react';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { DeleteOutlined, HolderOutlined } from '@ant-design/icons';
import styled from 'styled-components';

import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

import { TOrbit } from '../../../i18n/TModules';
import { PlainButton } from '../../components/pageStyles';
import { describeTrigger, triggerErrors } from '../adapters/ruleAdapter';
import StepPanel from '../kit/StepPanel';
import { AddButton } from '../kit/controls';
import { Hint } from '../kit/layout';
import {
  addTrigger,
  IWizardStepProps,
  MAX_QUEST_TASKS,
  moveItem,
  removeTrigger,
  WizardTrigger
} from '../wizardModel';
import { tint, wz, wzType } from '../wizardStyles';
import AddTriggerModal from './AddTriggerModal';

const TW = `${TOrbit}.wizard`;

const DRAG_TYPE = 'orbit-wizard-trigger';

// 6778:22112's task card: a number chip, the trigger and its summary, the handle and the bin.
const Card = styled.div<{ $over: boolean }>`
  display: flex;
  align-items: center;
  padding: 14px 16px;
  border: ${wz.line} solid ${({ $over }) => ($over ? wz.accent : wz.border)};
  border-radius: 12px;
  background: ${wz.field};

  & + & {
    margin-top: 12px;
  }

  > * + * {
    margin-left: 14px;
  }
`;

const NumberChip = styled.span`
  display: inline-flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: ${tint(wz.accent, 0.14)};
  color: ${wz.accent};
  font-weight: 700;
  ${wzType.label}
`;

const Summary = styled(PlainButton)`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-width: 0;
  color: ${wz.text};
  ${wzType.body}

  > span + span {
    margin-top: 4px;
    color: ${wz.secondary};
    ${wzType.label}
  }
`;

const Points = styled.span`
  flex: none;
  padding: 3px 9px;
  border-radius: 999px;
  background: ${tint(wz.accent, 0.14)};
  color: ${wz.accent};
  font-weight: 700;
  ${wzType.label}
`;

const IconButton = styled(PlainButton)`
  flex: none;
  color: ${wz.muted};
  font-size: 16px;

  &:hover,
  &:focus-visible {
    color: ${wz.text};
  }
`;

const Foot = styled.div`
  display: flex;
  align-items: center;
  margin-top: 14px;

  > * + * {
    margin-left: 12px;
  }
`;

interface ITriggerCardProps {
  index: number;
  trigger: WizardTrigger;
  progress: boolean;
  noun: 'task' | 'rule';
  removable: boolean;
  onMove: (from: number, to: number) => void;
  onEdit: () => void;
  onRemove: () => void;
}

const TriggerCard: React.FC<ITriggerCardProps> = ({
  index,
  trigger,
  progress,
  noun,
  removable,
  onMove,
  onEdit,
  onRemove
}) => {
  const { t } = useMultiLanguageTranslation();
  const cardRef = useRef<HTMLDivElement>(null);
  const handleRef = useRef<HTMLButtonElement>(null);
  const [{ over }, drop] = useDrop({
    accept: DRAG_TYPE,
    collect: monitor => ({ over: monitor.isOver() }),
    drop: (item: { index: number }) => onMove(item.index, index)
  });
  const [, drag, preview] = useDrag({ type: DRAG_TYPE, item: { index } });
  drop(preview(cardRef));
  drag(handleRef);
  const { title, detail } = describeTrigger(trigger, t);
  const n = index + 1;
  const plural = noun === 'task' ? 'tasks' : 'rules';

  return (
    <Card ref={cardRef} $over={over} data-testid="orbit-wizard-trigger-card">
      <NumberChip>{n}</NumberChip>
      <Summary type="button" onClick={onEdit}>
        <span>{title}</span>
        {detail !== '' ? <span>{detail}</span> : null}
      </Summary>
      {progress && trigger.points !== '' ? (
        <Points>{t(`${TW}.summary.points`, { value: trigger.points })}</Points>
      ) : null}
      {noun === 'task' ? (
        <IconButton
          ref={handleRef}
          type="button"
          aria-label={t(`${TW}.tasks.reorder`, { n })}
          onKeyDown={event => {
            if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
              event.preventDefault();
              onMove(index, event.key === 'ArrowUp' ? index - 1 : index + 1);
            }
          }}
          style={{ cursor: 'grab' }}
        >
          <HolderOutlined aria-hidden="true" />
        </IconButton>
      ) : null}
      {removable ? (
        <IconButton
          type="button"
          aria-label={t(`${TW}.${plural}.remove`, { n })}
          onClick={onRemove}
        >
          <DeleteOutlined aria-hidden="true" />
        </IconButton>
      ) : null}
    </Card>
  );
};

/**
 * Step 3/5's list: Quest tasks (6778:22112, reorderable, at most 10) or Progress rules
 * (6778:23978). Adding one opens the Add Task picker (6778:22346), then its trigger form.
 */
const TriggerListStep: React.FC<IWizardStepProps> = ({ state, update, next, back }) => {
  const { t } = useMultiLanguageTranslation();
  const [adding, setAdding] = useState(false);
  const quest = state.type === 'quest';
  const noun = quest ? 'task' : 'rule';
  const plural = quest ? 'tasks' : 'rules';
  const type = state.type ?? 'quest';
  const full = quest && state.triggers.length >= MAX_QUEST_TASKS;
  const errors = state.triggers.flatMap((trigger, index) =>
    triggerErrors(trigger, type, t).map(
      error => `${t(`${TW}.review.${noun}`, { n: index + 1 })}: ${error}`
    )
  );

  return (
    <StepPanel
      step={quest ? 'tasks' : 'rules'}
      // 6985:4280 / :4760 redraw both lists in the 950 panel.
      narrow
      title={t(`${TW}.create.${type}`)}
      question={t(`${TW}.${plural}.question`)}
      errors={errors}
      onNext={next}
      onBack={back}
    >
      <DndProvider backend={HTML5Backend}>
        {state.triggers.map((trigger, index) => (
          <TriggerCard
            // Order is the identity here: a move re-numbers every card.
            key={index}
            index={index}
            trigger={trigger}
            progress={!quest}
            noun={noun}
            removable={state.triggers.length > 1}
            onMove={(from, to) =>
              update(current => ({ ...current, triggers: moveItem(current.triggers, from, to) }))
            }
            onEdit={() => update(current => ({ ...current, editing: index, step: 'trigger' }))}
            onRemove={() => update(current => removeTrigger(current, index))}
          />
        ))}
      </DndProvider>
      <Foot>
        <AddButton
          label={t(`${TW}.${plural}.add`)}
          disabled={full}
          onClick={() => setAdding(true)}
        />
        {full ? <Hint>{t(`${TW}.tasks.max`, { max: MAX_QUEST_TASKS })}</Hint> : null}
      </Foot>
      {adding ? (
        <AddTriggerModal
          title={t(`${TW}.${plural}.modalTitle`)}
          addLabel={t(`${TW}.${plural}.add`)}
          onClose={() => setAdding(false)}
          onAdd={kind => update(current => addTrigger(current, kind))}
        />
      ) : null}
    </StepPanel>
  );
};

export default TriggerListStep;
